import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useQuery,
  getOnboardingStatus,
  getLocationTypesForDropdown,
  completeInstanceOnboarding,
  skipInstanceOnboarding,
} from "wasp/client/operations";
import { Building2, Plus, Trash2 } from "lucide-react";

type BuildingDraft = {
  key: string;
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  typeSlug: string;
};

function newBuilding(defaultTypeSlug: string): BuildingDraft {
  return {
    key: Math.random().toString(36).slice(2),
    name: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    typeSlug: defaultTypeSlug,
  };
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { data: status, isLoading } = useQuery(getOnboardingStatus);
  const { data: allTypes } = useQuery(getLocationTypesForDropdown);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [buildings, setBuildings] = useState<BuildingDraft[]>([]);

  const onboarding = status?.onboarding;
  const mode = onboarding?.mode || "multi";

  const typeOptions = useMemo(() => {
    const types = (allTypes || []) as {
      id: string;
      name: string;
      slug: string;
    }[];
    const allowed: string[] = onboarding?.allowedTypeSlugs || [];
    if (!allowed.length) return types;
    return types.filter((t) => allowed.includes(t.slug));
  }, [allTypes, onboarding?.allowedTypeSlugs]);

  useEffect(() => {
    if (status?.onboarding && !initialized) {
      setBuildings([newBuilding(status.onboarding.defaultTypeSlug)]);
      setInitialized(true);
    }
  }, [status, initialized]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!onboarding) return;
    setBusy(true);
    setError("");
    try {
      await completeInstanceOnboarding({
        buildings: buildings.map((b) => ({
          name: b.name,
          address: b.address,
          city: b.city,
          state: b.state,
          country: b.country,
          postalCode: b.postalCode,
          typeSlug: b.typeSlug || onboarding.defaultTypeSlug,
        })),
      });
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setError(err?.message || "Could not finish onboarding");
    } finally {
      setBusy(false);
    }
  }

  async function onSkip() {
    setBusy(true);
    setError("");
    try {
      await skipInstanceOnboarding();
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setError(err?.message || "Could not skip onboarding");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading || !status || !onboarding) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 text-neutral-600">
        Loading onboarding…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="mx-auto max-w-3xl px-4 py-12">
        <div className="mb-8 flex items-start gap-4">
          <div className="rounded-2xl bg-sky-100 p-3 text-sky-700">
            <Building2 className="h-7 w-7" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">
              {status.portalPackLabel} · First launch
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-neutral-900">
              {onboarding.title}
            </h1>
            <p className="mt-2 text-neutral-600">{onboarding.subtitle}</p>
            {status.brandName && (
              <p className="mt-1 text-sm text-neutral-500">
                Portal: <span className="font-medium">{status.brandName}</span>
              </p>
            )}
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-6 rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm"
        >
          {buildings.map((building, index) => (
            <div
              key={building.key}
              className="space-y-3 rounded-2xl border border-neutral-100 bg-neutral-50/70 p-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-neutral-800">
                  {mode === "single"
                    ? "Building details"
                    : `Building ${index + 1}`}
                </h2>
                {mode === "multi" && buildings.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setBuildings((rows) =>
                        rows.filter((r) => r.key !== building.key),
                      )
                    }
                    className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Remove
                  </button>
                )}
              </div>

              <label className="block text-sm">
                <span className="mb-1 block font-medium">Name</span>
                <input
                  required
                  value={building.name}
                  onChange={(e) =>
                    setBuildings((rows) =>
                      rows.map((r) =>
                        r.key === building.key
                          ? { ...r, name: e.target.value }
                          : r,
                      ),
                    )
                  }
                  className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2"
                  placeholder={
                    mode === "single"
                      ? "Aurora Tower"
                      : "e.g. Main Library"
                  }
                />
              </label>

              {(mode === "multi" ||
                (onboarding.allowedTypeSlugs?.length || 0) > 1) && (
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Building type</span>
                  <select
                    value={building.typeSlug}
                    onChange={(e) =>
                      setBuildings((rows) =>
                        rows.map((r) =>
                          r.key === building.key
                            ? { ...r, typeSlug: e.target.value }
                            : r,
                        ),
                      )
                    }
                    className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2"
                  >
                    {typeOptions.map((t) => (
                      <option key={t.slug} value={t.slug}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="block text-sm">
                <span className="mb-1 block font-medium">Street address</span>
                <input
                  value={building.address}
                  onChange={(e) =>
                    setBuildings((rows) =>
                      rows.map((r) =>
                        r.key === building.key
                          ? { ...r, address: e.target.value }
                          : r,
                      ),
                    )
                  }
                  className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2"
                  placeholder="123 Main Street"
                />
              </label>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">City</span>
                  <input
                    required
                    value={building.city}
                    onChange={(e) =>
                      setBuildings((rows) =>
                        rows.map((r) =>
                          r.key === building.key
                            ? { ...r, city: e.target.value }
                            : r,
                        ),
                      )
                    }
                    className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2"
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">
                    State / province
                  </span>
                  <input
                    required
                    value={building.state}
                    onChange={(e) =>
                      setBuildings((rows) =>
                        rows.map((r) =>
                          r.key === building.key
                            ? { ...r, state: e.target.value }
                            : r,
                        ),
                      )
                    }
                    className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2"
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Country</span>
                  <input
                    required
                    value={building.country}
                    onChange={(e) =>
                      setBuildings((rows) =>
                        rows.map((r) =>
                          r.key === building.key
                            ? { ...r, country: e.target.value }
                            : r,
                        ),
                      )
                    }
                    className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2"
                    placeholder="Canada"
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Postal code</span>
                  <input
                    value={building.postalCode}
                    onChange={(e) =>
                      setBuildings((rows) =>
                        rows.map((r) =>
                          r.key === building.key
                            ? { ...r, postalCode: e.target.value }
                            : r,
                        ),
                      )
                    }
                    className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2"
                  />
                </label>
              </div>
            </div>
          ))}

          {mode === "multi" &&
            buildings.length < (onboarding.maxBuildings || 25) && (
              <button
                type="button"
                onClick={() =>
                  setBuildings((rows) => [
                    ...rows,
                    newBuilding(onboarding.defaultTypeSlug),
                  ])
                }
                className="inline-flex items-center gap-2 rounded-xl border border-dashed border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
              >
                <Plus className="h-4 w-4" />
                Add another building
              </button>
            )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={busy}
              className="rounded-xl bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-neutral-800 disabled:opacity-50"
            >
              {busy ? "Saving…" : "Finish setup"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onSkip}
              className="rounded-xl px-4 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
            >
              Skip for now
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
